import { useRef } from "react";
import { motion } from "framer-motion";
import {
  Gem, Palette, Target, TrendingUp, PenTool, Megaphone,
  Radio, Code, BarChart3, Sparkles, Handshake, Users, ArrowUpRight,
} from "lucide-react";
import { SERVICES } from "../lib/data";
import ServiceCanvas from "./three/ServiceCanvas";

const ICONS = {
  gem: Gem, palette: Palette, target: Target, "trending-up": TrendingUp,
  "pen-tool": PenTool, megaphone: Megaphone, radio: Radio, code: Code,
  "bar-chart-3": BarChart3, sparkles: Sparkles, handshake: Handshake, users: Users,
};

const MODEL_BY_ICON = {
  gem: "gem", palette: "easel", target: "target", "trending-up": "seo",
  "pen-tool": "funnel", megaphone: "social", radio: "pr", code: "web",
  "bar-chart-3": "analytics", sparkles: "experiential", handshake: "partnerships", users: "influencer",
};

function TiltCard({ service, index }) {
  const ref = useRef(null);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    const rotX = (0.5 - y) * 10;
    const rotY = (x - 0.5) * 10;
    el.style.transform = `perspective(900px) rotateX(${rotX}deg) rotateY(${rotY}deg) translateZ(6px)`;
    el.style.setProperty("--mx", `${x * 100}%`);
    el.style.setProperty("--my", `${y * 100}%`);
  };
  const onLeave = () => {
    const el = ref.current;
    if (el) el.style.transform = "perspective(900px) rotateX(0) rotateY(0)";
  };

  const Icon = ICONS[service.icon] || Sparkles;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
    >
      <div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        data-testid={`service-card-${service.n}`}
        className="group relative h-full rounded-2xl border border-white/10 bg-[var(--bg-secondary)] p-7 md:p-8 transition-[transform] duration-200 will-change-transform overflow-hidden"
        style={{ transformStyle: "preserve-3d" }}
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{
            background:
              "radial-gradient(400px circle at var(--mx,50%) var(--my,50%), rgba(108,43,217,0.18), transparent 60%)",
          }}
        />
        {/* 3D model stage */}
        <div className="relative flex items-start justify-between">
          <div className="h-40 md:h-44 w-full -mx-2">
            <ServiceCanvas
              type={MODEL_BY_ICON[service.icon] || "gem"}
              fallback={
                <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-[var(--accent-primary)]/15 text-[var(--accent-primary-hover)]">
                  <Icon size={26} strokeWidth={1.75} />
                </div>
              }
            />
          </div>
          <span className="absolute top-0 right-0 font-display text-sm font-semibold text-zinc-600">{service.n}</span>
        </div>
        <h3 className="relative mt-2 font-display text-xl font-semibold leading-tight text-white">
          {service.title}
        </h3>
        <p className="relative mt-2 text-sm font-medium text-[var(--accent-secondary)]">
          {service.tag}
        </p>
        <p className="relative mt-3 text-sm leading-relaxed text-zinc-400">
          {service.desc}
        </p>
        <a
          href="#contact"
          className="relative mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-white group-hover:gap-3 transition-all"
        >
          Get Started
          <ArrowUpRight size={16} className="text-[var(--accent-secondary)]" />
        </a>
      </div>
    </motion.div>
  );
}

export default function Services() {
  return (
    <section
      id="what-we-do"
      data-testid="services-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-[var(--bg-primary)]"
    >
      <div className="mb-14 md:mb-20 max-w-4xl">
        <p className="text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-[var(--accent-primary)]">
          What We Do
        </p>
        <h2 className="mt-5 font-display text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02] text-white">
          Twelve services.<br />
          <span className="text-zinc-500">One relentless</span> partner.
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
        {SERVICES.map((s, i) => (
          <TiltCard key={s.n} service={s} index={i} />
        ))}
      </div>

      {/* CTA band */}
      <motion.a
        href="#contact"
        data-testid="services-cta-band"
        data-magnetic
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="group mt-16 md:mt-24 flex flex-col md:flex-row items-center justify-between gap-6 rounded-3xl border border-white/10 bg-[var(--bg-secondary)] px-8 md:px-14 py-12 md:py-16 relative overflow-hidden"
      >
        <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-[radial-gradient(circle,rgba(231,0,138,0.28),transparent_60%)] blur-2xl" />
        <h3 className="relative font-display text-3xl md:text-5xl font-bold tracking-tight text-white text-center md:text-left">
          Book a free consultation today!
        </h3>
        <span className="relative inline-flex items-center gap-3 rounded-full bg-[var(--accent-secondary)] px-8 py-4 text-sm font-semibold text-white group-hover:gap-5 transition-all">
          Let&apos;s talk <ArrowUpRight size={18} />
        </span>
      </motion.a>
    </section>
  );
}
